// NIP-15 Marketplace types and helpers

export interface ShippingZone {
  id: string;
  name?: string;
  cost: number;
  regions: string[];
}

export interface Stall {
  id: string;
  name: string;
  description?: string;
  currency: string;
  shipping: ShippingZone[];
}

export interface ProductSpec {
  key: string;
  value: string;
}

export interface ProductShipping {
  id: string;   // must match a stall shipping zone id
  cost: number; // extra cost on top of the stall base cost
}

export interface Product {
  id: string;
  stall_id: string;
  name: string;
  description?: string;
  images?: string[];
  currency: string;
  price: number;
  quantity: number | null; // null = unlimited
  specs?: ProductSpec[];
  shipping?: ProductShipping[];
  categories?: string[]; // -> 't' tags
}

// ── Checkout (NIP-15 type-0 order, sent via NIP-04 DM) ──────────────────────

export interface OrderContact {
  nostr: string;       // 32-byte hex pubkey
  phone?: string;
  email?: string;
}

export interface OrderItem {
  product_id: string;
  quantity: number;
}

export interface Order {
  id: string;
  type: 0;
  name?: string;
  address?: string;
  message?: string;
  contact: OrderContact;
  items: OrderItem[];
  shipping_id: string;
}

// ── Payment request (type 1, sent by merchant) ────────────────────────────

export interface PaymentOption {
  type: 'url' | 'btc' | 'ln' | 'lnurl';
  link: string;
}

export interface PaymentRequest {
  id: string;
  type: 1;
  message?: string;
  payment_options: PaymentOption[];
}

// ── Order status update (type 2, sent by merchant) ────────────────────────

export interface OrderStatus {
  id: string;
  type: 2;
  message: string;
  paid: boolean;
  shipped: boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────

/** Generate a random UUID-like id for stalls / products / orders */
export function generateId(): string {
  return crypto.randomUUID();
}

/** Build the kind:30017 event content from a Stall object */
export function stallToContent(stall: Stall): string {
  return JSON.stringify(stall);
}

/** Build the kind:30018 event content from a Product object */
export function productToContent(product: Product): string {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { categories: _cats, ...rest } = product;
  return JSON.stringify(rest);
}

/** Parse a stall event's content safely */
export function parseStall(content: string): Stall | null {
  try {
    const data = JSON.parse(content) as Stall;
    if (!data.id || !data.name || !data.currency) return null;
    return data;
  } catch {
    return null;
  }
}

/** Parse a product event's content safely */
export function parseProduct(content: string): Product | null {
  try {
    const data = JSON.parse(content) as Product;
    if (!data.id || !data.stall_id || !data.name) return null;
    return data;
  } catch {
    return null;
  }
}

/** All the supported currencies */
export const CURRENCIES = [
  { value: 'SATS', label: 'Satoshis (SATS)' },
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'USD', label: 'US Dollar (USD)' },
  { value: 'EUR', label: 'Euro (EUR)' },
  { value: 'GBP', label: 'British Pound (GBP)' },
  { value: 'CAD', label: 'Canadian Dollar (CAD)' },
  { value: 'AUD', label: 'Australian Dollar (AUD)' },
  { value: 'JPY', label: 'Japanese Yen (JPY)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'BRL', label: 'Brazilian Real (BRL)' },
  { value: 'MXN', label: 'Mexican Peso (MXN)' },
  { value: 'INR', label: 'Indian Rupee (INR)' },
  { value: 'NGN', label: 'Nigerian Naira (NGN)' },
  { value: 'ZAR', label: 'South African Rand (ZAR)' },
];

/** Common shipping regions */
export const SHIPPING_REGIONS = [
  'Worldwide',
  'North America',
  'United States',
  'Canada',
  'Europe',
  'United Kingdom',
  'Germany',
  'France',
  'Australia',
  'Asia',
  'Japan',
  'Digital / No Shipping',
];

/** Format a price with currency */
export function formatPrice(price: number, currency: string): string {
  if (currency === 'SATS') return `${price.toLocaleString()} sats`;
  if (currency === 'BTC') return `${price} BTC`;
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
  } catch {
    return `${price} ${currency}`;
  }
}
